/**
 * @fileoverview Component for displaying available cost centers
 * Shows list of cost centers that can be dragged to the sequence
 */

import React from 'react';
import { ListGroup, ListGroupItem, Badge } from 'reactstrap';
import { CentroCosto } from '../types/collections/CentroCosto';

/**
 * Props for CentroCostoList component
 */
export interface CentroCostoListProps {
  /** List of available cost centers */
  centrosCosto: CentroCosto[];
  /** Callback when drag starts */
  onDragStart: (centroCostoId: string) => void;
  /** Whether the component is in read-only mode */
  readOnly?: boolean;
}

/**
 * Component for displaying available cost centers
 * Each item can be dragged to add to the sequence
 */
export const CentroCostoList: React.FC<CentroCostoListProps> = ({
  centrosCosto,
  onDragStart,
  readOnly = false
}) => {
  /**
   * Handle drag start for a cost center
   * @param e - Drag event
   * @param centroCostoId - ID of the cost center being dragged
   */
  const handleDragStart = (e: React.DragEvent, centroCostoId: string) => {
    if (readOnly) return;
    
    e.dataTransfer.setData('text/plain', centroCostoId);
    e.dataTransfer.effectAllowed = 'copy';
    
    // Add visual feedback
    e.currentTarget.classList.add('dragging');
  };

  /**
   * Handle drag end
   * @param e - Drag event
   */
  const handleDragEnd = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('dragging');
  };

  /**
   * Handle click to add (alternative to drag)
   * @param centroCostoId - ID of the cost center to add
   */
  const handleClick = (centroCostoId: string) => {
    if (readOnly) return;
    onDragStart(centroCostoId);
  };

  /**
   * Get machine name for display
   * @param centro - Cost center object
   * @returns Machine name or "Sin máquina"
   */
  const getMachineName = (centro: CentroCosto): string => {
    // Verificar si hay datos expandidos
    if (centro.expand && centro.expand.maquina) {
      return centro.expand.maquina.nombre || 'Sin nombre';
    }
    
    // Verificar si hay un objeto maquina directo
    if (centro.maquina && typeof centro.maquina === 'object' && 'nombre' in centro.maquina) {
      return (centro.maquina as any).nombre as string;
    }
    
    return 'Sin máquina';
  };

  /**
   * Get user name for display
   * @param centro - Cost center object
   * @returns User name or "Sin encargado"
   */
  const getUserName = (centro: CentroCosto): string => {
    // Verificar si hay datos expandidos
    if (centro.expand && centro.expand.encargado) {
      return centro.expand.encargado.name || 'Sin nombre';
    }
    
    // Verificar si hay un objeto encargado directo
    if (centro.encargado && typeof centro.encargado === 'object' && 'name' in centro.encargado) {
      return (centro.encargado as any).name as string;
    }
    
    return 'Sin encargado';
  };

  /**
   * Get process name for display
   * @param centro - Cost center object
   * @returns Process name or empty string
   */
  const getProcessName = (centro: CentroCosto): string => {
    // Verificar si hay datos expandidos
    if (centro.expand && centro.expand.proceso) {
      return centro.expand.proceso.nombre || '';
    }
    
    return '';
  };

  if (centrosCosto.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <i className="fas fa-inbox fa-2x mb-2"></i>
        <p className="mb-0">No hay centros de costo disponibles</p>
        <small>Todos los centros de costo ya están en la secuencia</small>
      </div>
    );
  }

  return (
    <div className="centro-costo-list">
      <ListGroup>
        {centrosCosto.map((centro) => (
          <ListGroupItem
            key={centro.id}
            className={`list-group-item-action d-flex justify-content-between align-items-center ${
              readOnly ? 'disabled' : 'draggable'
            }`}
            draggable={!readOnly}
            onDragStart={(e) => handleDragStart(e, centro.id)}
            onDragEnd={handleDragEnd}
            onClick={() => handleClick(centro.id)}
            style={{ cursor: readOnly ? 'default' : 'grab' }}
            title={readOnly ? 'Modo solo lectura' : 'Arrastra para agregar a la secuencia'}
          >
            <div className="d-flex flex-column flex-grow-1">
              <div className="d-flex align-items-center">
                <span className="fw-bold me-2">{centro.nombre}</span>
                {!readOnly && (
                  <i className="fas fa-grip-vertical text-muted me-2"></i>
                )}
              </div>
              
              <div className="d-flex align-items-center mt-1">
                <small className="text-muted me-3">
                  <i className="fas fa-cog me-1"></i>
                  {getMachineName(centro)}
                </small>
                
                <small className="text-muted me-3">
                  <i className="fas fa-user me-1"></i>
                  {getUserName(centro)}
                </small>
                
                <small className="text-muted">
                  <i className="fas fa-cogs me-1"></i>
                  {getProcessName(centro) || ""}
                </small>
              </div>
            </div>

            {!readOnly && (
              <div className="d-flex align-items-center">
                <Badge color="primary" className="me-2">
                  <i className="fas fa-plus"></i>
                </Badge>
                <small className="text-muted">Arrastra</small>
              </div>
            )}
          </ListGroupItem>
        ))}
      </ListGroup>

      {/* Summary */}
      <div className="mt-3 text-center">
        <small className="text-muted">
          {centrosCosto.length} centro(s) de costo disponible(s)
        </small>
      </div>
    </div>
  );
};
